import classNames from 'classnames';
import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';

import {
  DEAL_PROCESS_NAME,
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  getProcess,
  isBookingProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { subtractTime } from '../../util/dates';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LISTING_UNIT_TYPES,
  STOCK_MULTIPLE_ITEMS,
  propTypes,
} from '../../util/types';

import customerLogo from '../../assets/customerLogo.png';
import merchatLogo from '../../assets/merchatLogo.png';
import {
  Avatar,
  H2,
  IconSpinner,
  LayoutSideNavigation,
  NamedLink,
  NotificationBadge,
  Page,
  PaginationLinks,
  ResponsiveImage,
  TimeRange,
  UserDisplayName
} from '../../components';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { Events, mixPanelEventsConstants, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';
import { transitions as dealTransitions } from '../../transactions/transactionProcessDeal';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './InboxPage.module.css';
import { getStateData, stateDataShape } from './InboxPage.stateData';
const { UUID } = sdkTypes;


// Check if the transaction line-items use booking-related units
const getUnitLineItem = lineItems => {
  const unitLineItem = lineItems?.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  return unitLineItem;
};

// Booking data (start & end) are bit different depending on display times and
// if "end" refers to last day booked or the first exclusive day
const bookingData = (tx, lineItemUnitType, timeZone) => {
  // Attributes: displayStart and displayEnd can be used to differentiate shown time range
  // from actual start and end times used for availability reservation. It can help in situations
  // where there are preparation time needed between bookings.
  // Read more: https://www.sharetribe.com/api-reference/marketplace.html#bookings
  const { start, end, displayStart, displayEnd } = tx?.booking?.attributes || {};
  const bookingStart = displayStart || start;
  const bookingEndRaw = displayEnd || end;

  // When unit type is night, we can assume booking end to be inclusive.
  const isNight = lineItemUnitType === LINE_ITEM_NIGHT;
  const isHour = lineItemUnitType === LINE_ITEM_HOUR;
  const bookingEnd =
    isNight || isHour ? bookingEndRaw : subtractTime(bookingEndRaw, 1, 'days', timeZone);

  return { bookingStart, bookingEnd };
};

const BookingTimeInfoMaybe = props => {
  const { transaction, ...rest } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  const process = getProcess(processName);
  const isInquiry = process.getState(transaction) === process.states.INQUIRY;

  if (isInquiry) {
    return null;
  }

  const hasLineItems = transaction?.attributes?.lineItems?.length > 0;
  const unitLineItem = hasLineItems
    ? transaction.attributes?.lineItems?.find(
      item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
    )
    : null;

  const lineItemUnitType = unitLineItem ? unitLineItem.code : null;
  const dateType = lineItemUnitType === LINE_ITEM_HOUR ? DATE_TYPE_DATETIME : DATE_TYPE_DATE;

  const timeZone = transaction?.listing?.attributes?.availabilityPlan?.timezone || 'Etc/UTC';
  const { bookingStart, bookingEnd } = bookingData(transaction, lineItemUnitType, timeZone);

  return (
    <TimeRange
      startDate={bookingStart}
      endDate={bookingEnd}
      dateType={dateType}
      timeZone={timeZone}
      {...rest}
    />
  );
};

BookingTimeInfoMaybe.propTypes = {
  transaction: propTypes.transaction.isRequired,
};

export const InboxItem = props => {
  const {
    transactionRole,
    tx,
    intl,
    stateData,
    isBooking,
    stockType = STOCK_MULTIPLE_ITEMS,
  } = props;
  const { customer, provider, listing } = tx;
  const { processName, processState, actionNeeded, isSaleNotification, isFinal } = stateData;
  const isCustomer = transactionRole === TX_TRANSITION_ACTOR_CUSTOMER;

  const lineItems = tx.attributes?.lineItems;
  const hasPricingData = lineItems?.length > 0;
  const unitLineItem = getUnitLineItem(lineItems);
  const quantity = hasPricingData && !isBooking ? unitLineItem.quantity.toString() : null;
  const showStock = stockType === STOCK_MULTIPLE_ITEMS || (quantity && unitLineItem.quantity > 1);

  const otherUser = isCustomer ? provider : customer;
  const otherUserDisplayName = <UserDisplayName user={otherUser} intl={intl} />;
  const businessName = isCustomer
    ? otherUser?.attributes?.profile?.publicData?.business_name || null
    : null;

  const isOtherUserBanned = otherUser.attributes.banned;

  const { zohoInvoiceId } = tx?.attributes?.metadata || {};

  const rowNotificationDot = isSaleNotification ? <div className={css.notificationDot} /> : null;

  const linkClasses = classNames(css.itemLink, {
    [css.bannedUserLink]: isOtherUserBanned,
  });
  const stateClasses = classNames(css.stateName, {
    [css.stateConcluded]: isFinal,
    [css.stateActionNeeded]: actionNeeded,
    [css.stateNoActionNeeded]: !actionNeeded,
  });

  return (
    <div className={css.item}>
      <div className={css.bookingTable}>
        <NamedLink
          className={linkClasses}
          name={isCustomer ? 'OrderDetailsPage' : 'SaleDetailsPage'}
          params={{ id: tx.id.uuid }}
        >
          <table>
            <tbody>
              <tr>
                <td>
                  <span className={css.heading}>
                    <div className={css.rowNotificationDot}>{rowNotificationDot}</div>
                    <div className={css.itemAvatar}>
                      {otherUser?.profileImage ?
                        <Avatar user={otherUser} showMerchantProfile={isCustomer} />
                        :
                        (isCustomer ?
                          <ResponsiveImage localImageSrc={customerLogo} variants={'custom-image'} />
                          : <ResponsiveImage localImageSrc={merchatLogo} variants={'custom-image'} />
                        )
                      }
                    </div>
                  </span>
                </td>
                <td>
                  {otherUserDisplayName}
                </td>
                <td>{(isCustomer || !isBooking) ? listing?.attributes?.title : otherUserDisplayName}</td>
                <td className={isCustomer && !isBooking ? css.shrinkInnerColumn : css.increaseColumn}>
                  {isBooking ? (
                    <BookingTimeInfoMaybe transaction={tx} />
                  ) : hasPricingData && showStock ? (
                    <FormattedMessage id="InboxPage.quantity1" values={{ quantity }} />
                  ) : null}
                </td>
                <td>
                  <div className={stateClasses}>
                    <FormattedMessage
                      id={`InboxPage.${processName}.${processState}.status`}
                      values={{ transactionRole }}
                    />
                  </div>
                </td>
                {!isCustomer ?
                  <td>
                    <div className={stateClasses}>
                      {zohoInvoiceId || "N/A"}
                    </div>
                  </td>
                  : null}
              </tr>
            </tbody>
          </table>
        </NamedLink>
      </div>
    </div>
  );
};

InboxItem.propTypes = {
  transactionRole: oneOf([TX_TRANSITION_ACTOR_CUSTOMER, TX_TRANSITION_ACTOR_PROVIDER]).isRequired,
  tx: propTypes.transaction.isRequired,
  intl: intlShape.isRequired,
  stateData: stateDataShape.isRequired,
};

export const InboxPageComponent = props => {
  const config = useConfiguration();
  const {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    intl,
    pagination,
    params,
    providerNotificationCount,
    scrollingDisabled,
    transactions,
  } = props;
  const { tab, state: ordersOrBookings } = params;
  const validTab = tab === 'orders' || tab === 'sales';
  if (!validTab) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }
  const isBooking = ordersOrBookings === 'bookings';
  const currentUserId = currentUser?.id?.uuid;
  const userType = currentUser && currentUser?.attributes?.profile?.publicData?.userType;

  useEffect(() => {
    if (currentUser && userType == 'customer') {
      if (isBooking) {
        trackEventsMixpanel(Events.CUSTOMER_BOOKINGS_CALENDAR,
          {
            action_type: mixPanelEventsData.CUSTOMER_VIEW_CALENDAR,
            screen: mixPanelEventsScreens.INBOX_PAGE,
            user_type: 'customer',
            user_id: currentUserId,
          }
        );
      }
      else {
        // trackEventsMixpanel(mixPanelEventsConstants.ORDERS, {
        //   screen: mixPanelEventsScreens.INBOX_PAGE,
        //   user_id: currentUser.id.uuid,
        //   action_type: mixPanelEventsConstants.VIEW_ORDERS_LIST,
        //   user_type: 'Customer'
        // })
      }
    }
    if (currentUser && userType == 'provider') {
      if (ordersOrBookings == 'bookings') {
        // trackEventsMixpanel(mixPanelEventsConstants.BOOKINGS, {
        //   screenName: mixPanelEventsScreens.INBOX_PAGE,
        //   action_type: mixPanelEventsConstants.VIEW_BOOKINGS,
        //   user_id: currentUser.id.uuid,
        //   user_type: 'Provider'
        // })
      }
    }
  }, [currentUserId]);

  const isOrders = tab === 'orders';
  const hasNoResults = !fetchInProgress && transactions.length === 0 && !fetchOrdersOrSalesError;
  const ordersTitle = intl.formatMessage({ id: 'InboxPage.ordersTitle' });
  const salesTitle = intl.formatMessage({ id: 'InboxPage.salesTitle' });
  const title = isOrders ? ordersTitle : salesTitle;

  const pickType = lt => conf => conf.listingType === lt;
  const findListingTypeConfig = publicData => {
    const listingTypeConfigs = config.listing?.listingTypes;
    const { listingType } = publicData || {};
    const foundConfig = listingTypeConfigs?.find(pickType(listingType));
    return foundConfig;
  };
  const toTxItem = tx => {
    const transactionRole = isOrders ? TX_TRANSITION_ACTOR_CUSTOMER : TX_TRANSITION_ACTOR_PROVIDER;
    let stateData = null;
    try {
      stateData = getStateData({ transaction: tx, transactionRole, intl });
    } catch (error) {
      // If stateData is missing, omit the transaction from InboxItem list.
    }

    const publicData = tx?.listing?.attributes?.publicData || {};
    const foundListingTypeConfig = findListingTypeConfig(publicData);
    const { transactionType, stockType } = foundListingTypeConfig || {};
    const process = tx?.attributes?.processName || transactionType?.transactionType;
    const transactionProcess = resolveLatestProcessName(process);
    const isBooking = isBookingProcess(transactionProcess);
    // Render InboxItem only if the latest transition of the transaction is handled in the `txState` function.
    return stateData ? (
      <li key={tx.id.uuid} className={css.listItem}>
        <InboxItem
          transactionRole={transactionRole}
          tx={tx}
          intl={intl}
          stateData={stateData}
          stockType={stockType}
          isBooking={isBooking}
        />
      </li>
    ) : null;
  };

  const hasOrderOrSaleTransactions = (tx, isOrdersTab, user) => {
    return isOrdersTab
      ? user?.id && tx && tx.length > 0 && tx[0].customer.id.uuid === user?.id?.uuid
      : user?.id && tx && tx.length > 0 && tx[0].provider.id.uuid === user?.id?.uuid;
  };
  const hasTransactions =
    !fetchInProgress && hasOrderOrSaleTransactions(transactions, isOrders, currentUser);

  const tabs = [
    {
      text: (
        <span>
          <FormattedMessage id="InboxPage.ordersTabTitle" />
        </span>
      ),
      selected: isOrders,
      linkProps: {
        name: 'InboxPage',
        params: { tab: 'orders' },
      },
    },
    {
      text: (
        <span>
          <FormattedMessage id="InboxPage.salesTabTitle" />
          {providerNotificationCount > 0 ? (
            <NotificationBadge count={providerNotificationCount} />
          ) : null}
        </span>
      ),
      selected: !isOrders,
      linkProps: {
        name: 'InboxPage',
        params: { tab: 'sales' },
      },
    },
  ];

  const transactionRole = isOrders ? TX_TRANSITION_ACTOR_CUSTOMER : TX_TRANSITION_ACTOR_PROVIDER;
  const isCustomer = transactionRole === TX_TRANSITION_ACTOR_CUSTOMER;
  const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation
        containerClassName={classNames(css.inboxPageSideNav, arabicLanguage && css.arabicText)}
        sideNavClassName={css.navigation}
        mainColumnClassName={css.mainColumnInboxPage}
        rootClassName={css.rootSideNav}
        topbar={
          <TopbarContainer
            mobileRootClassName={css.mobileTopbar}
            desktopClassName={css.desktopTopbar}
          />
        }
        sideNav={
          <>{isBooking ?
            <H2 as="h1" className={css.title}>
              <FormattedMessage id="InboxPage.Bookings" />
            </H2>
            :
            <H2 as="h1" className={css.title}>
              <FormattedMessage id="InboxPage.Orders" />
            </H2>
          }
            {/* <TabNav rootClassName={css.tabs} tabRootClassName={css.tab} tabs={tabs} />{' '} */}
          </>
        }
        footer={<FooterContainer />}
      >
        {fetchOrdersOrSalesError ? (
          <p className={css.error}>
            <FormattedMessage id="InboxPage.fetchFailed" />
          </p>
        ) : null}

        <div className={classNames(css.tableWrapper, isBooking ? null : css.orderTableWrapper)}>
          <ul className={css.itemList}>
            <div className={css.bookingTable}>
              <table>
                <thead>
                  <tr>
                    <th><FormattedMessage id="InboxPage.imageHeading" /></th>
                    <th><FormattedMessage id="InboxPage.nameHeading" /></th>
                    <th> <FormattedMessage id={isBooking ? (isCustomer ? "InboxPage.titleHeading" : "InboxPage.customerHeading") : "InboxPage.productHeading"} /></th>
                    <th className={isCustomer && !isBooking ? css.shrinkColumn : css.columnDefault}> <FormattedMessage id={isBooking ? "InboxPage.bookingTimeHeading" : "InboxPage.noOfItemsHeading"} /></th>
                    <th><FormattedMessage id="InboxPage.statusHeading" /></th>
                    {!isCustomer ? <th><FormattedMessage id="InboxPage.invoiceIdHeading" /></th> : null}
                  </tr>
                </thead>
              </table>
            </div>
            {!fetchInProgress ? (
              transactions.map(toTxItem)
            ) : (
              <li className={css.listItemsLoading}>
                <IconSpinner />
              </li>
            )}
            {hasNoResults ? (
              <li key="noResults" className={css.noResults}>
                <FormattedMessage
                  id={isOrders ? 'InboxPage.noOrdersFound' : 'InboxPage.noSalesFound'}
                />
              </li>
            ) : null}
          </ul>
        </div>
        {hasTransactions && pagination && pagination.totalPages > 1 ? (
          <PaginationLinks
            className={css.pagination}
            pageName={ordersOrBookings ? "InboxNewPage" : "InboxPage"}
            pagePathParams={params}
            pagination={pagination}
          />
        ) : null}
      </LayoutSideNavigation>
    </Page>
  );
};

InboxPageComponent.defaultProps = {
  currentUser: null,
  currentUserHasOrders: null,
  fetchOrdersOrSalesError: null,
  pagination: null,
  providerNotificationCount: 0,
  sendVerificationEmailError: null,
};

InboxPageComponent.propTypes = {
  params: shape({
    tab: string.isRequired,
  }).isRequired,

  currentUser: propTypes.currentUser,
  fetchInProgress: bool.isRequired,
  fetchOrdersOrSalesError: propTypes.error,
  pagination: propTypes.pagination,
  providerNotificationCount: number,
  scrollingDisabled: bool.isRequired,
  transactions: arrayOf(propTypes.transaction).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { fetchInProgress, fetchOrdersOrSalesError, pagination, transactionRefs } = state.InboxPage;
  const { currentUser, currentUserNotificationCount: providerNotificationCount } = state.user;

  const allTransactions = getMarketplaceEntities(state, transactionRefs);
  // Hides any child transaction from the Inbox list -- these exist purely as
  // internal bookkeeping (a deal's per-listing child, or the negotiated
  // flow's zero-priced staff-calendar-blocking child, see
  // CheckoutPageTransactionHelpers.js) and were never meant to be a
  // customer/provider-facing conversation of their own.
  // protectedData.parentTransactionId is the generic marker both patterns
  // set on the child (see eventHandlers.js, TransactionPage.duck.js); the
  // deal-specific transition check is kept alongside it rather than
  // replaced, since it's the original mechanism and removing it isn't
  // needed to fix this.
  const onlyServiceNDeals = allTransactions.filter(tx =>
    !tx?.attributes?.protectedData?.parentTransactionId &&
    !tx?.attributes?.transitions?.some(t =>
      t.transition === dealTransitions.CHILD_PRODUCT_REQUEST_PAYMENT ||
      t.transition === dealTransitions.CHILD_SERVICE_REQUEST_PAYMENT
    )
  );

  const getBookingFromChildTransaction = (state, childTransactionIds) => {
    const childTransactionRefs = childTransactionIds.map(
      id => ({ id: new UUID(id), type: "transaction" })
    );

    const childTransactions = getMarketplaceEntities(state, childTransactionRefs);

    return childTransactions.find(childTx =>
      childTx?.attributes?.transitions?.some(t =>
        t.transition === dealTransitions.CHILD_PRODUCT_REQUEST_PAYMENT ||
        t.transition === dealTransitions.CHILD_SERVICE_REQUEST_PAYMENT
      )
    )?.booking;
  };

  const updatedBookings = onlyServiceNDeals?.map(tx => {
    const { processName, protectedData, lineItems = [] } = tx?.attributes || {};
    const { dealChildTransactions = [] } = protectedData || {};
    const isDealBooking = processName === DEAL_PROCESS_NAME;

    let updatedLineItems = [...lineItems];

    if (isDealBooking) {
      // Modify the line item code for deals
      updatedLineItems = lineItems.map(item => {
        if (item?.code === 'line-item/day') {
          return {
            ...item,
            code: 'line-item/hour'
          };
        }
        return item;
      });

      // Handle child booking replacement
      if (dealChildTransactions.length > 0) {
        const childBooking = getBookingFromChildTransaction(state, dealChildTransactions);
        if (childBooking) {
          return {
            ...tx,
            booking: childBooking,
            attributes: {
              ...tx.attributes,
              lineItems: updatedLineItems
            }
          };
        }
      }

      // If no child booking, still update line items
      return {
        ...tx,
        attributes: {
          ...tx.attributes,
          lineItems: updatedLineItems
        }
      };
    }

    // If not a deal booking, return as-is
    return { ...tx };
  });

  return {
    currentUser,
    fetchInProgress,
    fetchOrdersOrSalesError,
    pagination,
    providerNotificationCount,
    scrollingDisabled: isScrollingDisabled(state),
    transactions: updatedBookings,
  };
};

const InboxPage = compose(connect(mapStateToProps), injectIntl)(InboxPageComponent);

export default InboxPage;
